import { CANNON, DT, PLAYER, SELF_CANNON, SHIP } from "./config.ts";
import { clamp, localToWorld, type Pose, type Vec3 } from "./math.ts";
import { SHIP_POINTS } from "./ship.ts";
import { getIslandCannon, type IslandId } from "./world.ts";

/**
 * Cannon aiming and ballistics, shared so the server simulates exactly the
 * trajectory the client previews with its landing ring.
 */

export interface CannonAim {
  /** Ship-relative yaw of the barrel (0 = bow, π/2 = starboard). */
  yaw: number;
  /** Barrel elevation above horizontal, radians. */
  elevation: number;
}

export interface Ballistic {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
}

/** Clamps a gunner's ship-relative look into the arc of cannon `index`. */
export function cannonAim(index: number, yawLocal: number, pitch: number, out: CannonAim): CannonAim {
  const mount = SHIP_POINTS.cannons[index];
  if (!mount) throw new Error(`bad cannon ${index}`);
  return clampAim((mount.side * Math.PI) / 2, yawLocal, pitch, out);
}

export function islandCannonAim(id: IslandId, yawWorld: number, pitch: number, out: CannonAim): CannonAim {
  return clampAim(getIslandCannon(id).heading, yawWorld, pitch, out);
}

function clampAim(centre: number, yaw: number, pitch: number, out: CannonAim): CannonAim {
  const off = Math.atan2(Math.sin(yaw - centre), Math.cos(yaw - centre));
  out.yaw = centre + clamp(off, -CANNON.yawLimit, CANNON.yawLimit);
  out.elevation = clamp(-pitch, CANNON.minElevation, CANNON.maxElevation);
  return out;
}

const aim: CannonAim = { yaw: 0, elevation: 0 };
const pivot: Vec3 = { x: 0, y: 0, z: 0 };
const inherited: Vec3 = { x: 0, y: 0, z: 0 };

function muzzle(p: Vec3, yaw: number, elevation: number, distance: number, speed: number,
  inheritedVelocity: Readonly<Vec3>, outPos: Vec3, outVel: Vec3): void {
  const ce = Math.cos(elevation);
  const dx = Math.sin(yaw) * ce, dy = Math.sin(elevation), dz = Math.cos(yaw) * ce;
  outPos.x = p.x + dx * distance;
  outPos.y = p.y + dy * distance;
  outPos.z = p.z + dz * distance;
  outVel.x = dx * speed + inheritedVelocity.x;
  outVel.y = dy * speed + inheritedVelocity.y;
  outVel.z = dz * speed + inheritedVelocity.z;
}

/**
 * Muzzle position and launch velocity (world) of a ball fired from cannon
 * `index` on `ship`, aimed by the gunner's ship-relative yaw and pitch.
 */
export function cannonShot(ship: Pose & { speed: number }, index: number, yawLocal: number, pitch: number, outPos: Vec3, outVel: Vec3): void {
  const mount = SHIP_POINTS.cannons[index]!;
  cannonAim(index, yawLocal, pitch, aim);
  localToWorld(ship, mount.x, SHIP.deckHeight + CANNON.pivotHeight, mount.z, pivot);
  inherited.x = Math.sin(ship.heading) * ship.speed;
  inherited.y = 0;
  inherited.z = Math.cos(ship.heading) * ship.speed;
  muzzle(pivot, aim.yaw + ship.heading, aim.elevation, CANNON.barrelLength, CANNON.ballSpeed, inherited, outPos, outVel);
}

/** Capsule feet and world velocity for a player, clear of the barrel and source rail. */
export function playerCannonShot(pivot: Vec3, yawWorld: number, elevation: number,
  inheritedVelocity: Readonly<Vec3>, outFeet: Vec3, outVelocity: Vec3): void {
  muzzle(pivot, yawWorld, elevation, CANNON.barrelLength + SELF_CANNON.muzzleClearance,
    SELF_CANNON.speed, inheritedVelocity, outFeet, outVelocity);
  outFeet.y -= PLAYER.height / 2;
}

/** Matches fixed-step semi-implicit Euler, not the continuous cannonball parabola. */
export function playerLaunchPositionAt(ball: Ballistic, steps: number, out: Vec3): Vec3 {
  const t = steps * DT;
  out.x = ball.x + ball.vx * t;
  out.y = ball.y + ball.vy * t - SELF_CANNON.gravity * DT * DT * steps * (steps + 1) / 2;
  out.z = ball.z + ball.vz * t;
  return out;
}

/** Ball position `t` seconds after launch. */
export function ballPositionAt(ball: Ballistic, t: number, out: Vec3): Vec3 {
  out.x = ball.x + ball.vx * t;
  out.y = ball.y + ball.vy * t - (CANNON.gravity * t * t) / 2;
  out.z = ball.z + ball.vz * t;
  return out;
}
